import React from 'react';
import { FileSpreadsheet, FileText, FileJson, Download } from 'lucide-react';

const ExportData = ({ survey }) => {
  const responses = survey.responses || [];
  const questions = survey.questions || [];

  const getAnswerValue = (question, answer) => {
    if (answer === undefined || answer === null || answer === '') return '';

    switch (question.type) {
      case 'multipleChoice':
      case 'dropdown':
        const option = question.options?.find(o => o.id === answer);
        return option?.text || answer;
      case 'checkboxes':
        if (!Array.isArray(answer)) return '';
        return answer.map(a => {
          const opt = question.options?.find(o => o.id === a);
          return opt?.text || a;
        }).join('; ');
      case 'likertScale':
        return question.labels?.[answer] || answer;
      case 'yesNo':
        return answer === 'yes' ? 'Yes' : 'No';
      default:
        return String(answer);
    }
  };

  const exportToCSV = () => {
    const headers = ['Response ID', 'Submitted At', ...questions.map((q, i) => `Q${i + 1}: ${q.text.substring(0, 50)}`)];
    
    const rows = responses.map(response => {
      return [
        response.responseId,
        new Date(response.submittedAt).toISOString(),
        ...questions.map(q => getAnswerValue(q, response.answers?.[q.id]))
      ];
    });

    const csvContent = [
      headers.map(h => `"${h.replace(/"/g, '""')}"`).join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    downloadFile(csvContent, `${survey.title}_responses.csv`, 'text/csv');
  };

  const exportToJSON = () => {
    const data = {
      survey: {
        id: survey.id,
        title: survey.title,
        description: survey.description,
        createdAt: survey.createdAt,
        questions: questions.map(q => ({
          id: q.id,
          type: q.type,
          text: q.text,
          options: q.options
        }))
      },
      responses: responses.map(r => ({
        responseId: r.responseId,
        submittedAt: r.submittedAt,
        answers: r.answers,
        completionTime: r.completionTime
      })),
      exportedAt: new Date().toISOString()
    };

    downloadFile(JSON.stringify(data, null, 2), `${survey.title}_data.json`, 'application/json');
  };

  const exportSummaryReport = () => {
    let report = `# Survey Report: ${survey.title}\n\n`;
    report += `Generated: ${new Date().toLocaleString()}\n\n`;
    report += `## Summary\n`;
    report += `- Total Responses: ${responses.length}\n`;
    report += `- Total Questions: ${questions.length}\n`;
    report += `- Survey Status: ${survey.status}\n\n`;
    report += `## Questions and Responses\n\n`;

    questions.forEach((question, index) => {
      report += `### Q${index + 1}: ${question.text}\n`;
      report += `Type: ${question.type}\n\n`;

      const answers = responses.map(r => r.answers?.[question.id]).filter(a => a !== undefined);
      
      if (['multipleChoice', 'dropdown', 'yesNo', 'checkboxes'].includes(question.type)) {
        const counts = {};
        answers.forEach(a => {
          if (Array.isArray(a)) {
            a.forEach(item => {
              counts[item] = (counts[item] || 0) + 1;
            });
          } else {
            counts[a] = (counts[a] || 0) + 1;
          }
        });
        
        Object.entries(counts).forEach(([key, count]) => {
          const label = question.options?.find(o => o.id === key)?.text || key;
          const pct = ((count / answers.length) * 100).toFixed(1);
          report += `- ${label}: ${count} (${pct}%)\n`;
        });
      } else if (['linearScale', 'likertScale'].includes(question.type)) {
        const nums = answers.map(a => parseInt(a)).filter(n => !isNaN(n));
        if (nums.length > 0) {
          const avg = nums.reduce((s, n) => s + n, 0) / nums.length;
          report += `- Average: ${avg.toFixed(2)}\n`;
          report += `- Responses: ${nums.length}\n`;
        }
      } else {
        report += `- ${answers.length} text responses collected\n`;
      }
      
      report += '\n';
    });

    downloadFile(report, `${survey.title}_report.txt`, 'text/plain');
  };

  const downloadFile = (content, filename, type) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename.replace(/[^a-z0-9._-]/gi, '_');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="export-section">
      <h3 style={{ margin: '0 0 20px', color: '#1e293b' }}>Export Options</h3>
      
      <div className="export-options">
        <button className="export-option" onClick={exportToCSV}>
          <div className="export-icon">
            <FileSpreadsheet size={24} />
          </div>
          <div className="export-info">
            <h4>Export to CSV</h4>
            <p>Download responses as a spreadsheet (Excel compatible)</p>
          </div>
          <Download size={20} style={{ color: '#94a3b8' }} />
        </button>

        <button className="export-option" onClick={exportToJSON}>
          <div className="export-icon">
            <FileJson size={24} />
          </div>
          <div className="export-info">
            <h4>Export to JSON</h4>
            <p>Download complete survey data in JSON format</p>
          </div>
          <Download size={20} style={{ color: '#94a3b8' }} />
        </button>

        <button className="export-option" onClick={exportSummaryReport}>
          <div className="export-icon">
            <FileText size={24} />
          </div>
          <div className="export-info">
            <h4>Summary Report</h4>
            <p>Download a text summary of all responses</p>
          </div>
          <Download size={20} style={{ color: '#94a3b8' }} />
        </button>
      </div>

      <div className="export-stats" style={{ marginTop: 32 }}>
        <h4 style={{ margin: '0 0 12px', color: '#475569' }}>Data Overview</h4>
        <div style={{ display: 'flex', gap: 24 }}>
          <div className="export-stat">
            <div className="stat-value">{responses.length}</div>
            <div className="stat-label">Total Responses</div>
          </div>
          <div className="export-stat">
            <div className="stat-value">{questions.length}</div>
            <div className="stat-label">Questions</div>
          </div>
        </div>
      </div>

      <style>{`
        .export-stat {
          text-align: center;
          padding: 16px 24px;
          background: #f8fafc;
          border-radius: 8px;
        }
        .stat-value {
          font-size: 28px;
          font-weight: 700;
          color: #6366f1;
        }
        .stat-label {
          font-size: 13px;
          color: #64748b;
          margin-top: 4px;
        }
      `}</style>
    </div>
  );
};

export default ExportData;
